<template>
  <div class="model-table-box">
    <div class="common-table-box">
      <el-table
        class="publish-table"
        :header-row-class-name="customHeader"
        ref="modelTable"
        :data="dataList"
        highlight-selection-row
        height="100%"
        @select="selectChange"
        @select-all="selectAll"
        @cell-click="handleClick">
        <el-table-column
          type="selection"
          width="60"></el-table-column>
        <el-table-column
          label="序号"
          type="index"
          width="55">
          <template #default="scope">
            <span class="serialNumber">
              {{ scope.$index + 1 }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          label="名称/大小/格式"
          :width="getColumnWidth('name')">
          <template v-slot="{ row }">
            <div :class="`icon-column ${isDisable(row) ? 'disabled' : ''}`">
              <div
                v-if="getIcon(row).isShow"
                class="icon-column-center">
                <icon-svg
                  :class="`${getIcon(row).className}`"
                  :title="getIcon(row).title"
                  :icon-class="getIcon(row).icon" />
              </div>
              <div class="content-box text-overflow">
                <div
                  class="name text-overflow"
                  v-permission="getTablePermission()"
                  @click="modelView(row)">
                  <span :title="row?.nodeName">{{ row?.nodeName }}</span>
                </div>
                <div
                  class="details"
                  v-if="row?.nodeType === 2 && schemeType !== 'Wmts'">
                  <span>
                    大小：{{
                      `${formatFileSize(row.length).fileSize}${
                        formatFileSize(row.length).unit
                      }`
                    }}
                  </span>
                  <span class="file-type">格式：{{ getFileType(row) }}</span>
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          label="转换状态"
          min-width="160px"
          v-if="isConvertType()">
          <template v-slot="{ row }">
            <div v-if="row?.nodeType === 1 || row?.status === 6">--</div>
            <div
              v-else-if="row?.status === 0"
              class="status-text">
              <div class="circle bg-color1"></div>
              <span class="color1">未转换</span>
            </div>
            <div
              v-else-if="row?.status === 1"
              class="status-text">
              <div class="circle bg-color2"></div>
              <span class="color2">排队中</span>
            </div>
            <div
              v-else-if="row?.status === 4"
              class="status-text">
              <div class="circle bg-color3"></div>
              <span class="color3">转换失败</span>
            </div>
            <div
              v-else-if="row?.status === 5"
              class="status-text">
              <div class="circle bg-color4"></div>
              <span class="color4">已转换</span>
            </div>
            <div
              v-else-if="row.status === 2"
              class="common-progress-box">
              <el-progress
                :show-text="false"
                :percentage="row.rate"
                key="28897" />
              <div
                class="custom-text"
                key="28898">
                <div class="text">{{ `${row.rate ? row.rate : 0}%` }}</div>
                <div class="progress-name-box text-overflow">
                  {{ row.rateMsg ? row.rateMsg : '' }}
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          :label="`创建日期${isConvertType() ? '/转换时间' : ''}`"
          :width="getColumnWidth('time')">
          <template v-slot="{ row }">
            <div class="timetransfer">
              {{
                row?.lastModificationTime
                  ? formatTime(row?.lastModificationTime)
                  : formatTime(row?.creationTime)
              }}
            </div>
            <div
              class="timetransfer"
              v-if="isConvertType() && row?.nodeType === 2">
              {{ formatSecond(row.convertDuration) }}
            </div>
          </template>
        </el-table-column>
        <el-table-column
          min-width="100"
          label="版本号"
          v-if="schemeType === 'Bim'">
          <template slot="header">
            <span>版本号</span>
            <icon-svg
              class="version-icon"
              title="版本号代表文件转换成功的次数"
              icon-class="ic-banbenhao" />
          </template>
          <template slot-scope="scope">
            <span
              class="version"
              v-if="scope.row?.successVersion"
              >{{ scope.row?.successVersion }}</span
            >
            <span
              class="version"
              v-else
              >--</span
            >
          </template>
        </el-table-column>
        <el-table-column
          label="状态"
          v-if="schemeType === 'Wmts'">
          <template v-slot="{ row }">
            <div
              v-if="row.metadataStatus === 6"
              class="status-text">
              <div class="circle bg-color2"></div>
              <span class="color2">解析中</span>
            </div>
            <div
              v-else-if="row.metadataStatus === 4"
              class="status-text">
              <div class="circle bg-color4"></div>
              <span class="color4">解析成功</span>
            </div>
            <div
              v-else-if="row.metadataStatus === 5"
              class="status-text">
              <div class="circle bg-color3"></div>
              <span class="color3">解析失败</span>
              <icon-svg
                icon-class="ic-zaicijiexi"
                class="recheck-icon"
                title="重新解析"
                @click.native="reCheckWmts(row)" />
            </div>
          </template>
        </el-table-column>
        <el-table-column
          label="坐标系"
          v-if="schemeType !== 'Panorama'">
          <template v-slot="{ row }">
            <div
              v-if="schemeType === 'Osgb'"
              class="coordinate-box">
              <div
                class="text-overflow"
                :title="getCoordinate(row)">
                {{ getCoordinate(row) }}
              </div>
              <el-tooltip
                :effect="$theme.tooltipKey()"
                v-if="row.readonlyCoordinate"
                placement="top-start"
                popper-class="common-panel-tooltip-box">
                <div slot="content">
                  倾斜摄影已进行过投影变换，无法更改坐标系，若要更改请使用最新版地形处理工具处理
                </div>
                <div
                  class="lock-icon-box"
                  title="">
                  <icon-svg icon-class="ic-suoding" />
                </div>
              </el-tooltip>
            </div>
            <span
              v-else
              class="text-overflow"
              :title="getCoordinate(row)">
              {{ getCoordinate(row) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          label="单位"
          v-if="schemeType === 'Cad'">
          <template v-slot="{ row }">
            {{ row.unit ? cadUnitMap[row.unit] : '--' }}
          </template>
        </el-table-column>
        <el-table-column
          label="操作"
          :width="operaWidth"
          v-if="isOperateVisible() && !isDemoProject()">
          <template v-slot="{ row }">
            <div
              class="text-center-l operation-cell custom-operation"
              v-if="row.isAuthorized">
              <el-button
                type="text"
                size="small"
                class="upload-text-btn noborder-btn"
                v-if="isButttonVisible(row, 'edit')"
                @click="editWMTS(row)"
                >编辑</el-button
              >
              <el-button
                type="text"
                size="small"
                class="noborder-btn"
                v-if="isButttonVisible(row, 'rename')"
                @click="reName(row)"
                >重命名</el-button
              >
              <el-button
                type="text"
                size="small"
                class="noborder-btn"
                v-if="isButttonVisible(row, 'moveTo')"
                @click="openMoveTreeDialog([row])"
                >移动到</el-button
              >
              <el-button
                v-if="isButttonVisible(row, 'submitConvert')"
                class="noborder-btn"
                key="submitConvert"
                type="text"
                size="small"
                :disabled="row?.status === 1 || row?.status === 2"
                @click="submitConvertFiles([row])"
                >转换</el-button
              >
              <el-button
                v-if="isButttonVisible(row, 'update')"
                key="update"
                type="text"
                size="small"
                class="upload-text-btn noborder-btn"
                :id="`refresh-${row.nodeId}`"
                @click="reUpload(row)"
                >更新</el-button
              >
              <el-button
                class="noborder-btn"
                v-if="isButttonVisible(row, 'deleteNode')"
                key="deleteNode"
                type="text"
                size="small"
                @click="deleteNodes([row])"
                :disabled="row?.status === 1 || row?.status === 2"
                >删除</el-button
              >
              <div
                class="common-dropdown-box"
                v-if="isBatchButttonVisible(row, 'fileButtons')">
                <el-dropdown
                  key="fileDropdown"
                  placement="bottom"
                  @visible-change="visible => handleVisibleChange(visible, row)"
                  @command="commond => fileBatchOperate(commond, [row])"
                  :disabled="row?.status === 1 || row?.status === 2">
                  <el-button
                    type="text"
                    class="noborder-btn"
                    size="small"
                    key="fileButtons"
                    :disabled="row?.status === 1 || row?.status === 2">
                    更多
                  </el-button>
                  <el-dropdown-menu
                    slot="dropdown"
                    class="common-dropdown-menu dropdown-primary dropdown-dark dropdown--table-primary">
                    <el-dropdown-item
                      v-if="
                        isButttonVisible(row, 'setCoordinate', 'fileButtons')
                      "
                      command="0"
                      >设置坐标系</el-dropdown-item
                    >
                    <el-dropdown-item
                      v-if="isButttonVisible(row, 'download', 'fileButtons')"
                      command="1"
                      >下载</el-dropdown-item
                    >
                    <el-dropdown-item
                      v-if="isButttonVisible(row, 'update', 'fileButtons')"
                      command="2"
                      class="upload-dropdown-item-box">
                      <el-button
                        type="text"
                        size="small"
                        class="upload-dropdown-btn"
                        :id="`refresh-${row.nodeId}`"
                        >更新</el-button
                      >
                    </el-dropdown-item>
                    <el-dropdown-item
                      v-if="isButttonVisible(row, 'moveTo', 'fileButtons')"
                      @click="moveNodes([row])"
                      command="4"
                      >移动到</el-dropdown-item
                    >
                    <el-dropdown-item
                      v-if="isShareBtnVisible(row, 'share', 'fileButtons')"
                      command="3"
                      >分享</el-dropdown-item
                    >
                    <el-dropdown-item
                      v-if="isButttonVisible(row, 'setUnit', 'fileButtons')"
                      command="5">
                      设置单位
                    </el-dropdown-item>
                    <el-dropdown-item
                      v-if="
                        isButttonVisible(row, 'versionManage', 'fileButtons') &&
                        row?.successVersion > 1
                      "
                      command="6"
                      >版本管理</el-dropdown-item
                    >
                    <el-dropdown-item
                      v-if="
                        isButttonVisible(row, 'rvtUvMap', 'fileButtons') &&
                        isRvt(row)
                      "
                      command="7">
                      外部贴图
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </div>
            </div>
            <div v-else>--</div>
          </template>
        </el-table-column>
        <el-table-column
          label="操作"
          width="150"
          v-if="isOperateVisible() && isDemoProject()">
          <template v-slot="{ row }">
            <div class="common-button-box text-center-l custom-operation">
              <el-button
                type="text"
                size="small"
                @click="downloadFile(row)">
                下载
              </el-button>
              <el-button
                v-if="
                  schemeType === 'Bim' &&
                  isChecked('Project.Single.Metadata.Bim.HisList')
                "
                type="text"
                size="small"
                :disabled="!(row?.successVersion > 1)"
                @click="viewVersion(row)">
                版本管理
              </el-button>
            </div>
          </template>
        </el-table-column>
        <template slot="empty">
          <div class="common-empty-box">
            <bj-empty class="empty-page">
              <template>
                <div class="empty-text">抱歉，没有找到相关内容</div>
              </template>
            </bj-empty>
          </div>
        </template>
      </el-table>
    </div>
    <div class="common-pagination-box pagination-primary">
      <el-pagination
        popper-class="common-pagination-popper-box"
        layout="sizes, prev, pager, next, jumper"
        :page-sizes="[10, 20, 50, 100, 150, 200]"
        :page-size="size"
        :current-page="page"
        :total="total"
        @size-change="sizeChange"
        @current-change="pageChange">
      </el-pagination>
    </div>
  </div>
</template>

<script>
import {
  formatFileSize,
  formatTime,
  formatSecond
} from '@/utils/generalMethods'

import { Store } from '@/utils/store'
import {
  getBimNodeList,
  getBimConvertConfigService,
  getBimDatasetFiles,
  getProjectStatus,
  batchGetProjectStatusService,
  getProjectsConvertingProgress,
  getRsNodeList,
  getDemDatasetFiles,
  getWmtsNodeList,
  reCheckWmtsService,
  getOsgbNodeList,
  getOsgbDatasetFiles,
  getPanoramaNodeList,
  getPanoramaDatasetFiles,
  getPointCloudNodeList,
  getPointCloudDatasetFiles,
  getCadNodeList,
  getVectorNodeListService,
  deleteBimDataSetCheckService,
  deleteBimFolderNode,
  deleteBimDataSetNode,
  deleteRsDataSetCheckService,
  deleteRsNode,
  getWmtsConfigService,
  deleteWmtsDataSetCheckService,
  deleteWmtsNode,
  deleteOsgbDataSetCheckService,
  deleteOsgbNode,
  deletePanoramaNode,
  deletePointCloudDataSetCheckService,
  deletePointCloudNode,
  deleteCadDataSetCheckService,
  deleteCadNode,
  getCadDatasetFiles,
  deleteVectorDataSetCheckService,
  deleteVectorNodeService,
  getVectorDatasetFilesServices,
  getHistoryVersion,
  isUpgradeService,
  getTextureList
} from '@/service/interface'
const ButtonsList = {
  Bim: [
    {
      name: 'rename',
      nodeType: [1],
      permissionCode: 'Project.Single.DataSet.BimTree.RenameNode'
    },
    {
      name: 'moveTo',
      nodeType: [1],
      permissionCode: 'Project.Single.DataSet.BimTree.MoveNode'
    },
    {
      name: 'deleteNode',
      nodeType: [1, 2],
      permissionCode: 'Project.Single.DataSet.BimTree.DeleteNode'
    },
    {
      name: 'submitConvert',
      nodeType: [2],
      permissionCode: 'Project.Single.Metadata.Bim.Convert'
    },
    {
      name: 'fileButtons',
      nodeType: [2],
      children: [
        {
          name: 'setCoordinate',
          permissionCode: 'Project.Single.DataSet.BimTree.RenameNode'
        },
        {
          name: 'download',
          permissionCode: 'Project.Single.Metadata.Bim.Download'
        },
        {
          name: 'update',
          permissionCode: 'Project.Single.Metadata.Bim.ReUpload'
        },
        {
          name: 'share',
          permissionCode: 'Project.Single.DataSet.Bim.Share'
        },
        {
          name: 'moveTo',
          permissionCode: 'Project.Single.DataSet.BimTree.MoveNode'
        },
        {
          name: 'versionManage',
          permissionCode: 'Project.Single.Metadata.Bim.HisList'
        },
        {
          name: 'rvtUvMap',
          permissionCode: 'Project.Single.Metadata.Bim.CofigConvert'
        }
      ]
    }
  ],
  Rs: [
    {
      name: 'rename',
      nodeType: [1],
      permissionCode: 'Project.Single.DataSet.RsTree.RenameNode'
    },
    {
      name: 'moveTo',
      nodeType: [1],
      permissionCode: 'Project.Single.DataSet.RsTree.MoveNode'
    },
    {
      name: 'deleteNode',
      nodeType: [1, 2],
      permissionCode: 'Project.Single.DataSet.RsTree.DeleteNode'
    },
    {
      name: 'update',
      nodeType: [2],
      permissionCode: 'Project.Single.Metadata.Rs.ReUpload'
    },
    {
      name: 'fileButtons',
      nodeType: [2],
      children: [
        {
          name: 'download',
          permissionCode: 'Project.Single.Metadata.Rs.Download'
        },
        {
          name: 'share',
          permissionCode: 'Project.Single.DataSet.Rs.Share'
        },
        {
          name: 'moveTo',
          permissionCode: 'Project.Single.DataSet.RsTree.MoveNode'
        }
        //   {
        //     name: "copyTo",
        //     permissionCode: "Project.Single.Metadata.Rs.copyTo",
        //   },
      ]
    }
  ],
  Wmts: [
    {
      name: 'rename',
      nodeType: [1],
      permissionCode: 'Project.Single.DataSet.WmtsTree.RenameNode'
    },
    {
      name: 'moveTo',
      nodeType: [1],
      permissionCode: 'Project.Single.DataSet.WmtsTree.MoveNode'
    },
    {
      name: 'deleteNode',
      nodeType: [1, 2],
      permissionCode: 'Project.Single.DataSet.WmtsTree.DeleteNode'
    },
    {
      name: 'edit',
      nodeType: [2],
      permissionCode: 'Project.Single.Metadata.Wmts.Config'
    },
    {
      name: 'fileButtons',
      nodeType: [2],
      children: [
        {
          name: 'share',
          permissionCode: 'Project.Single.DataSet.Wmts.Share'
        },
        {
          name: 'moveTo',
          permissionCode: 'Project.Single.DataSet.WmtsTree.MoveNode'
        }
        //   {
        //     name: "copyTo",
        //     permissionCode: "Project.Single.Metadata.WmtsTree.copyTo",
        //   },
      ]
    }
  ],
  Osgb: [
    {
      name: 'rename',
      nodeType: [1],
      permissionCode: 'Project.Single.DataSet.OsgbTree.RenameNode'
    },
    {
      name: 'moveTo',
      nodeType: [1],
      permissionCode: 'Project.Single.DataSet.OsgbTree.MoveNode'
    },
    {
      name: 'deleteNode',
      nodeType: [1, 2],
      permissionCode: 'Project.Single.DataSet.OsgbTree.DeleteNode'
    },
    {
      name: 'update',
      nodeType: [2],
      permissionCode: 'Project.Single.Metadata.Osgb.ReUpload'
    },
    {
      name: 'fileButtons',
      nodeType: [2],
      children: [
        {
          name: 'setCoordinate',
          permissionCode: 'Project.Single.DataSet.OsgbTree.SetCoordinates'
        },
        {
          name: 'download',
          permissionCode: 'Project.Single.Metadata.Osgb.Download'
        },
        {
          name: 'share',
          permissionCode: 'Project.Single.DataSet.Osgb.Share'
        },
        {
          name: 'moveTo',
          permissionCode: 'Project.Single.DataSet.OsgbTree.MoveNode'
        }
        //   {
        //     name: "copyTo",
        //     permissionCode: "Project.Single.Metadata.Osgb.copyTo",
        //   },
      ]
    }
  ],
  Panorama: [
    {
      name: 'rename',
      nodeType: [1],
      permissionCode: 'Project.Single.DataSet.PanoramaTree.RenameNode'
    },
    {
      name: 'moveTo',
      nodeType: [1],
      permissionCode: 'Project.Single.DataSet.PanoramaTree.MoveNode'
    },
    {
      name: 'deleteNode',
      nodeType: [1, 2],
      permissionCode: 'Project.Single.DataSet.PanoramaTree.DeleteNode'
    },
    {
      name: 'submitConvert',
      nodeType: [2],
      permissionCode: 'Project.Single.Metadata.Panorama.Convert'
    },
    {
      name: 'fileButtons',
      nodeType: [2],
      children: [
        {
          name: 'download',
          permissionCode: 'Project.Single.Metadata.Panorama.Download'
        },
        {
          name: 'update',
          permissionCode: 'Project.Single.Metadata.Panorama.ReUpload'
        },
        {
          name: 'share',
          permissionCode: 'Project.Single.DataSet.Panorama.Share'
        },
        {
          name: 'moveTo',
          permissionCode: 'Project.Single.DataSet.PanoramaTree.MoveNode'
        }
        //   {
        //     name: "copyTo",
        //     permissionCode: "Project.Single.Metadata.PanoramaTree.copyTo",
        //   },
      ]
    }
  ],
  PointCloud: [
    {
      name: 'rename',
      nodeType: [1],
      permissionCode: 'Project.Single.DataSet.PointCloudTree.RenameNode'
    },
    {
      name: 'moveTo',
      nodeType: [1],
      permissionCode: 'Project.Single.DataSet.PointCloudTree.MoveNode'
    },
    {
      name: 'deleteNode',
      nodeType: [1, 2],
      permissionCode: 'Project.Single.DataSet.PointCloudTree.DeleteNode'
    },
    {
      name: 'submitConvert',
      nodeType: [2],
      permissionCode: 'Project.Single.Metadata.PointCloud.Convert'
    },
    {
      name: 'fileButtons',
      nodeType: [2],
      children: [
        {
          name: 'setCoordinate',
          permissionCode: 'Project.Single.DataSet.PointCloudTree.RenameNode'
        },
        {
          name: 'download',
          permissionCode: 'Project.Single.Metadata.PointCloud.Download'
        },
        {
          name: 'update',
          permissionCode: 'Project.Single.Metadata.PointCloud.ReUpload'
        },
        {
          name: 'share',
          permissionCode: 'Project.Single.DataSet.PointCloud.Share'
        },
        {
          name: 'moveTo',
          permissionCode: 'Project.Single.DataSet.PointCloudTree.MoveNode'
        }
        //   {
        //     name: "copyTo",
        //     permissionCode: "Project.Single.Metadata.PointCloudTree.copyTo",
        //   },
      ]
    }
  ],
  Cad: [
    {
      name: 'rename',
      nodeType: [1],
      permissionCode: 'Project.Single.DataSet.CadTree.RenameNode'
    },
    {
      name: 'moveTo',
      nodeType: [1],
      permissionCode: 'Project.Single.DataSet.CadTree.MoveNode'
    },
    {
      name: 'deleteNode',
      nodeType: [1, 2],
      permissionCode: 'Project.Single.DataSet.CadTree.DeleteNode'
    },
    {
      name: 'submitConvert',
      nodeType: [2],
      permissionCode: 'Project.Single.Metadata.Cad.Convert'
    },
    {
      name: 'fileButtons',
      nodeType: [2],
      children: [
        {
          name: 'setCoordinate',
          permissionCode: 'Project.Single.DataSet.CadTree.MoveNode'
        },
        {
          name: 'download',
          permissionCode: 'Project.Single.Metadata.Cad.Download'
        },
        {
          name: 'update',
          permissionCode: 'Project.Single.Metadata.Cad.ReUpload'
        },
        {
          name: 'share',
          permissionCode: 'Project.Single.DataSet.Cad.Share'
        },
        {
          name: 'moveTo',
          permissionCode: 'Project.Single.DataSet.CadTree.MoveNode'
        },
        {
          name: 'setUnit',
          permissionCode: 'Project.Single.DataSet.CadTree.MoveNode'
        }
      ]
    }
  ],
  Vector: [
    {
      name: 'rename',
      nodeType: [1],
      permissionCode: 'Project.Single.DataSet.VectorTree.RenameNode'
    },
    {
      name: 'moveTo',
      nodeType: [1],
      permissionCode: 'Project.Single.DataSet.VectorTree.MoveNode'
    },
    {
      name: 'deleteNode',
      nodeType: [1, 2],
      permissionCode: 'Project.Single.DataSet.VectorTree.DeleteNode'
    },
    {
      name: 'submitConvert',
      nodeType: [2],
      permissionCode: 'Project.Single.Metadata.Vector.Convert'
    },
    {
      name: 'fileButtons',
      nodeType: [2],
      children: [
        {
          name: 'download',
          permissionCode: 'Project.Single.Metadata.Vector.Download'
        },
        {
          name: 'update',
          permissionCode: 'Project.Single.Metadata.Vector.Upload'
        },
        {
          name: 'share',
          permissionCode: 'Project.Single.DataSet.Vector.Share'
        },
        {
          name: 'moveTo',
          permissionCode: 'Project.Single.DataSet.VectorTree.MoveNode'
        }
      ]
    }
  ]
}
const FileTypesConfig = ['rvt', '3dxml', '3dm', 'skp']
const ConvertEnableDataType = ['Bim', 'Panorama', 'PointCloud', 'Cad', 'Vector']
const cadUnitMap = {
  Meter: '米',
  Centimeter: '厘米',
  Millimeter: '毫米',
  Kilometer: '千米',
  Inch: '英寸',
  Foot: '英尺',
  Mile: '英里'
}

export default {
  data() {
    return {
      dataList: [],
      // 用于处理转换服务停止之后再重启的情况
      convertingDataSets: [],
      selectDatasets: [],
      page: 1,
      size: 10,
      total: 0,
      tableRef: '',
      timer: null,
      customHeader: '',
      cadUnitMap
    }
  },
  props: {
    operaWidth: {
      type: [String, Number]
    },
    schemeType: String,
    rootNodeId: String,
    nodeId: String,
    name: String,
    customUploader: Object,
    updateModelTableVisible: Function,
    refreshCatalogTree: Function,
    handleCellClick: Function,
    reNameNode: Function,
    submitConvert: Function,
    openConfigDialog: Function,
    openMoveTreeDialog: Function,
    editWmtsMapService: Function,
    openModelVersionDialog: Function,
    openCoordinateDialog: Function,
    openShareDialog: Function,
    openSetUnitDialog: Function,
    openVectorConfigDialog: Function,
    openUvMapDialog: Function,
    openPointCloudConfigDialog: Function
  },
  mounted() {
    const currentBody = this.$refs.modelTable.bodyWrapper
    currentBody.addEventListener('scroll', ev => {
      const scrollTop = ev.target.scrollTop
      this.customHeader = scrollTop > 10 ? 'darkHeader' : ''
    })
    this.$eventManage.on('handleFileException', this.onHandleFileException)
  },
  beforeDestroy() {
    this.clearAllTimers()

    this.$eventManage.off('handleFileException', this.onHandleFileException)
  },
  methods: {
    getTablePermission() {
      const type = {
        Bim: 'Project.Single.Metadata.Bim.Preview',
        Rs: 'Project.Single.Metadata.Rs.Preview',
        Wmts: 'Project.Single.Metadata.Wmts.Preview',
        Osgb: 'Project.Single.Metadata.Osgb.Preview',
        Panorama: 'Project.Single.Metadata.Panorama.Preview',
        PointCloud: 'Project.Single.Metadata.PointCloud.Preview',
        Cad: 'Project.Single.Metadata.Cad.Preview',
        Vector: 'Project.Single.Metadata.Vector.Preview'
      }
      return type[this.schemeType]
    },
    pageChange(page) {
      this.page = page
      this.uploadDataList()
    },
    sizeChange(size) {
      this.size = size
      this.page = 1
      this.uploadDataList()
    },
    uploadDataList(page) {
      this.clearProgressTimer()

      return new Promise(resolve => {
        let pageNum = 0
        let pageInfo = sessionStorage.getItem('pageInfo')
        if (pageInfo) {
          pageInfo = JSON.parse(pageInfo)
          pageNum = Number(pageInfo.pageNum)
          this.size = Number(pageInfo.pageSize)
        } else if (page) {
          pageNum = page
        } else {
          pageNum = this.page
        }

        let params = {
          skipCount: (pageNum - 1) * this.size,
          resultCount: this.size,
          nodeId: this.nodeId,
          nodeName: this.name
        }
        switch (this.schemeType) {
          case 'Bim':
            getBimNodeList(params).then(result => {
              if (result.isSuccess) {
                let data = result.data
                let dataList = data.items

                dataList.forEach(item => {
                  let find = this.dataList.find(
                    el => el?.dataSetId === item?.dataSetId
                  )
                  if (find && find.rate) {
                    item.rate = find.rate
                    item.rateMsg = find.rateMsg
                  }
                })
                this.dataList = dataList

                this.total = data.totalCount
                this.page = pageNum
                this.selectDatasets = []

                this.progressTimer && clearInterval(this.progressTimer)
                let dataSets = dataList.filter(el => el?.nodeType === 2)
                // prettier-ignore
                dataSets = dataSets.filter(el => el?.status === 1 || el?.status === 2);
                if (dataSets.length) {
                  let dataSetIds = dataSets.map(el => el?.dataSetId)
                  // 防止nodeList接口返回的数据集状态不正确，导致出现死循环一直请求数据。通过转换状态接口进一步确认数据集的状态
                  // prettier-ignore
                  this.batchGetProjectStatus(dataSetIds).then((dataSetList) => {
                    const convertingStatus = [1, 2, 30, 60];
                    // prettier-ignore
                    dataSetList = dataSetList.filter((el) => convertingStatus.includes(el?.status));
                    let convertingDataSets = [];
                    dataSets.forEach((item) => {
                      let find = dataSetList.find(
                        (el) => el?.dataSetId === item?.dataSetId
                      );
                      if (find) {
                        convertingDataSets.push(item);
                      }
                    });
                    if (convertingDataSets.length) {
                      this.updateConvertProgress(convertingDataSets);
                      this.clearProgressTimer();
                      // prettier-ignore
                      this.progressTimer = setInterval(this.updateConvertProgress, 10000, convertingDataSets);
                    }
                  });
                }

                sessionStorage.removeItem('pageInfo')
                resolve(data.totalCount)
              } else {
                sessionStorage.removeItem('pageInfo')
                resolve(0)
              }
            })
            break
          case 'Rs':
            getRsNodeList(params).then(response => {
              if (response.isSuccess) {
                let data = response.data
                if (data && data.items) {
                  let dataList = data.items
                  this.dataList = dataList
                  this.total = data.totalCount
                  this.page = pageNum
                  this.selectDatasets = []

                  this.$nextTick(() => {
                    this.refeshDom()
                  })
                  sessionStorage.removeItem('pageInfo')
                  resolve(data.totalCount)
                } else {
                  this.dataList = []
                  this.total = 0
                  sessionStorage.removeItem('pageInfo')
                  resolve(0)
                }
              } else {
                sessionStorage.removeItem('pageInfo')
                resolve(0)
              }
            })
            break
          case 'Wmts':
            getWmtsNodeList(params).then(response => {
              if (response.isSuccess) {
                let data = response.data
                if (data && data.items) {
                  let dataList = data.items
                  this.dataList = dataList
                  this.total = data.totalCount
                  this.page = pageNum
                  this.selectDatasets = []

                  let checkingData = dataList.filter(
                    el => el.metadataStatus === 6
                  )
                  this.clearWmtsTimer()
                  if (checkingData.length) {
                    this.wmtsTimer = setInterval(this.uploadDataList, 1000)
                  }

                  sessionStorage.removeItem('pageInfo')
                  resolve(data.totalCount)
                } else {
                  this.dataList = []
                  this.total = 0
                  sessionStorage.removeItem('pageInfo')
                  resolve(0)
                }
              } else {
                sessionStorage.removeItem('pageInfo')
                resolve(0)
              }
            })
            break
          case 'Osgb':
            getOsgbNodeList(params).then(response => {
              if (response.isSuccess) {
                let data = response.data
                if (data && data.items) {
                  let dataList = data.items
                  this.dataList = dataList
                  this.total = data.totalCount
                  this.page = pageNum
                  this.selectDatasets = []

                  this.$nextTick(() => {
                    this.refeshDom()
                  })
                  sessionStorage.removeItem('pageInfo')
                  resolve(data.totalCount)
                } else {
                  this.dataList = []
                  this.total = 0
                  sessionStorage.removeItem('pageInfo')
                  resolve(0)
                }
              } else {
                sessionStorage.removeItem('pageInfo')
                resolve(0)
              }
            })
            break
          case 'Panorama':
            getPanoramaNodeList(params).then(response => {
              if (response.isSuccess) {
                let data = response.data
                if (data && data.items) {
                  let dataList = data.items
                  dataList.forEach(item => {
                    let find = this.dataList.find(
                      el => el?.dataSetId === item?.dataSetId
                    )
                    if (find && find.rate) {
                      item.rate = find.rate
                      item.rateMsg = find.rateMsg
                    }
                  })
                  this.dataList = dataList
                  this.total = data.totalCount
                  this.page = pageNum
                  this.selectDatasets = []

                  this.progressTimer && clearInterval(this.progressTimer)
                  let dataSets = dataList.filter(el => el?.nodeType === 2)
                  // prettier-ignore
                  dataSets = dataSets.filter(el => el?.status === 1 || el?.status === 2);
                  if (dataSets.length) {
                    let dataSetIds = dataSets.map(el => el?.dataSetId)
                    // 防止nodeList接口返回的数据集状态不正确，导致出现死循环一直请求数据。通过转换状态接口进一步确认数据集的状态
                    // prettier-ignore
                    this.batchGetProjectStatus(dataSetIds).then((dataSetList) => {
                      const convertingStatus = [1, 2, 30, 60];
                      // prettier-ignore
                      dataSetList = dataSetList.filter((el) => convertingStatus.includes(el?.status));
                      let convertingDataSets = [];
                      dataSets.forEach((item) => {
                        let find = dataSetList.find(
                          (el) => el?.dataSetId === item?.dataSetId
                        );
                        if (find) {
                          convertingDataSets.push(item);
                        }
                      });
                      if (convertingDataSets.length) {
                        this.updateConvertProgress(convertingDataSets);
                        this.clearProgressTimer();
                        // prettier-ignore
                        this.progressTimer = setInterval(this.updateConvertProgress, 10000, convertingDataSets);
                      }
                    });
                  }

                  sessionStorage.removeItem('pageInfo')
                  resolve(data.totalCount)
                } else {
                  this.dataList = []
                  this.total = 0
                  sessionStorage.removeItem('pageInfo')
                  resolve(0)
                }
              } else {
                sessionStorage.removeItem('pageInfo')
                resolve(0)
              }
            })
            break
          case 'PointCloud':
            getPointCloudNodeList(params).then(response => {
              if (response.isSuccess) {
                let data = response.data
                if (data && data.items) {
                  let dataList = data.items
                  dataList.forEach(item => {
                    let find = this.dataList.find(
                      el => el?.dataSetId === item?.dataSetId
                    )
                    if (find && find.rate) {
                      item.rate = find.rate
                      item.rateMsg = find.rateMsg
                    }
                  })
                  this.dataList = dataList
                  this.total = data.totalCount
                  this.page = pageNum
                  this.selectDatasets = []

                  this.progressTimer && clearInterval(this.progressTimer)
                  let dataSets = dataList.filter(el => el?.nodeType === 2)
                  // prettier-ignore
                  dataSets = dataSets.filter(el => el?.status === 1 || el?.status === 2);
                  if (dataSets.length) {
                    let dataSetIds = dataSets.map(el => el?.dataSetId)
                    // 防止nodeList接口返回的数据集状态不正确，导致出现死循环一直请求数据。通过转换状态接口进一步确认数据集的状态
                    // prettier-ignore
                    this.batchGetProjectStatus(dataSetIds).then((dataSetList) => {
                      const convertingStatus = [1, 2, 30, 60];
                      // prettier-ignore
                      dataSetList = dataSetList.filter((el) => convertingStatus.includes(el?.status));
                      let convertingDataSets = [];
                      dataSets.forEach((item) => {
                        let find = dataSetList.find(
                          (el) => el?.dataSetId === item?.dataSetId
                        );
                        if (find) {
                          convertingDataSets.push(item);
                        }
                      });
                      if (convertingDataSets.length) {
                        this.updateConvertProgress(convertingDataSets);
                        this.clearProgressTimer();
                        // prettier-ignore
                        this.progressTimer = setInterval(this.updateConvertProgress, 10000, convertingDataSets);
                      }
                    }
                    );
                  }

                  sessionStorage.removeItem('pageInfo')
                  resolve(data.totalCount)
                } else {
                  this.dataList = []
                  this.total = 0
                  sessionStorage.removeItem('pageInfo')
                  resolve(0)
                }
              } else {
                sessionStorage.removeItem('pageInfo')
                resolve(0)
              }
            })
            break
          case 'Cad':
            getCadNodeList(params).then(response => {
              if (response.isSuccess) {
                let data = response.data
                if (data && data.items) {
                  let dataList = data.items
                  this.dataList = dataList
                  this.total = data.totalCount
                  this.page = pageNum
                  this.selectDatasets = []

                  this.progressTimer && clearInterval(this.progressTimer)
                  let dataSets = dataList.filter(el => el?.nodeType === 2)
                  // prettier-ignore
                  dataSets = dataSets.filter(el => el?.status === 1 || el?.status === 2);
                  if (dataSets.length) {
                    let dataSetIds = dataSets.map(el => el?.dataSetId)
                    // 防止nodeList接口返回的数据集状态不正确，导致出现死循环一直请求数据。通过转换状态接口进一步确认数据集的状态
                    // prettier-ignore
                    this.batchGetProjectStatus(dataSetIds).then((dataSetList) => {
                      const convertingStatus = [1, 2, 30, 60];
                      // prettier-ignore
                      dataSetList = dataSetList.filter((el) => convertingStatus.includes(el?.status));
                      let convertingDataSets = [];
                      dataSets.forEach((item) => {
                        let find = dataSetList.find(
                          (el) => el?.dataSetId === item?.dataSetId
                        );
                        if (find) {
                          convertingDataSets.push(item);
                        }
                      });
                      if (convertingDataSets.length) {
                        this.updateConvertProgress(convertingDataSets);
                        this.clearProgressTimer();
                        // prettier-ignore
                        this.progressTimer = setInterval(this.updateConvertProgress, 10000, convertingDataSets);
                      }
                    }
                    );
                  }

                  sessionStorage.removeItem('pageInfo')
                  resolve(data.totalCount)
                } else {
                  this.dataList = []
                  this.total = 0
                  sessionStorage.removeItem('pageInfo')
                  resolve(0)
                }
              } else {
                sessionStorage.removeItem('pageInfo')
                resolve(0)
              }
            })
            break
          case 'Vector':
            getVectorNodeListService(params).then(response => {
              if (response.isSuccess) {
                let data = response.data
                if (data && data.items) {
                  let dataList = data.items

                  dataList.forEach(item => {
                    let find = this.dataList.find(
                      el => el?.dataSetId === item?.dataSetId
                    )
                    if (find && find.rate) {
                      item.rate = find.rate
                      item.rateMsg = find.rateMsg
                    }
                  })
                  this.dataList = dataList

                  this.total = data.totalCount
                  this.page = pageNum
                  this.selectDatasets = []

                  this.progressTimer && clearInterval(this.progressTimer)
                  let dataSets = dataList.filter(el => el?.nodeType === 2)
                  // prettier-ignore
                  dataSets = dataSets.filter(el => el?.status === 1 || el?.status === 2);
                  if (dataSets.length) {
                    let dataSetIds = dataSets.map(el => el?.dataSetId)
                    // 防止nodeList接口返回的数据集状态不正确，导致出现死循环一直请求数据。通过转换状态接口进一步确认数据集的状态
                    // prettier-ignore
                    this.batchGetProjectStatus(dataSetIds).then((dataSetList) => {
                      const convertingStatus = [1, 2, 30, 60];
                      // prettier-ignore
                      dataSetList = dataSetList.filter((el) => convertingStatus.includes(el?.status));
                      let convertingDataSets = [];
                      dataSets.forEach((item) => {
                        let find = dataSetList.find(
                          (el) => el?.dataSetId === item?.dataSetId
                        );
                        if (find) {
                          convertingDataSets.push(item);
                        }
                      });
                      if (convertingDataSets.length) {
                        this.updateConvertProgress(convertingDataSets);
                        this.clearProgressTimer();
                        // prettier-ignore
                        this.progressTimer = setInterval(this.updateConvertProgress, 10000, convertingDataSets);
                      }
                    }
                    );
                  }

                  sessionStorage.removeItem('pageInfo')
                  resolve(data.totalCount)
                } else {
                  this.dataList = []
                  this.total = 0
                  sessionStorage.removeItem('pageInfo')
                  resolve(0)
                }
              } else {
                sessionStorage.removeItem('pageInfo')
                resolve(0)
              }
            })
            break
          default:
            break
        }
      })
    },
    batchGetProjectStatus(dataSetIds) {
      return new Promise(resolve => {
        let params = { dataSetIds }
        batchGetProjectStatusService(params).then(response => {
          if (response.isSuccess) {
            let dataList = response.data
            resolve(dataList)
          } else {
            this.$message(response.errMsg)
          }
        })
      })
    },
    reName(nodeInfo) {
      this.reNameNode(nodeInfo)
    },
    reUpload(nodeInfo) {
      this.getFileInfo(nodeInfo).then(fileInfo => {
        this.customUploader.setStatus('update')
        this.customUploader.setCurrDataSetId(nodeInfo.parentId)
        fileInfo.formatType = nodeInfo.formatType
        this.customUploader.setCurrFileInfo(fileInfo)
      })
    },
    editWMTS(dataInfo) {
      if (!dataInfo.isNew) {
        this.$message.warning('该数据为旧数据，请使用新模板创建')
        return
      }

      let dataSetId = dataInfo?.dataSetId
      getWmtsConfigService({ dataSetId }).then(response => {
        if (response.isSuccess) {
          let mapInfo = response.data
          this.editWmtsMapService(mapInfo)
        } else {
          this.$message.error(response.errMsg)
        }
      })
    },
    batchSetCoordinate() {
      let datas = this.selectDatasets
      datas = datas.filter(el => el?.nodeType === 2)
      if (!datas.length) {
        this.$message.warning('请勾选需要设置坐标系的文件')
        return
      }

      if (this.schemeType === 'Osgb') {
        this.openCoordinateDialog(datas)
      } else {
        let datsSetIds = datas.map(el => el.nodeId)
        this.openCoordinateDialog(datsSetIds)
      }
    },
    batchShare() {
      let datas = this.selectDatasets
      datas = datas.filter(el => el?.nodeType === 2)
      if (!datas.length) {
        this.$message.warning('请勾选需要分享的文件')
        return
      }
      let invalidDataSets = []
      if (this.schemeType === 'Bim') {
        invalidDataSets = datas.filter(el => el?.successVersion < 1)
      } else {
        invalidDataSets = datas.filter(el => el?.status !== 5)
      }
      if (invalidDataSets.length) {
        let dataSetNameList = invalidDataSets.map(el => el?.nodeName)
        let warningMsg = this.getWarningMessage(
          dataSetNameList,
          '尚未转换成功，不能被分享'
        )
        this.$message.warning(warningMsg)
      }
      if (this.schemeType === 'Bim') {
        datas = datas.filter(el => el?.successVersion > 0)
      } else {
        datas = datas.filter(el => el?.status === 5)
      }

      this.openShareDialog(datas)
    },
    isNeedUpgrade(dataSets) {
      return new Promise(resolve => {
        let dataSetIds = dataSets.map(el => el.nodeId)
        isUpgradeService({ dataSetIds }).then(response => {
          if (response.isSuccess) {
            let data = response.data
            // 是否需要提示
            let flag = data.some(el => el.needUpgrade && el.versionNum > 1)
            if (flag) {
              let message = this.getUpgradeMessage(dataSets, data)
              this.$confirm(message, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
              })
                .then(() => {
                  resolve(true)
                })
                .catch(() => {
                  this.$message.info('取消转换')
                  resolve(false)
                })
            } else {
              resolve(true)
            }
          } else {
            this.$message.error(response.errMsg)
            resolve(false)
          }
        })
      })
    },
    getUpgradeMessage(dataSets, needUpgradeDataSets) {
      needUpgradeDataSets = needUpgradeDataSets.filter(el => el.needUpgrade)
      if (dataSets.length === 1) {
        // prettier-ignore
        let message = "为保证更好的用户体验，系统进行了重大升级。本次转换将会合并当前模型的全部历史版本，是否继续？";
        return message
      } else {
        const h = this.$createElement
        let message = h('p', null, [
          h(
            'div',
            null,
            '为保证更好的用户体验，系统进行了重大升级。本次转换将会合并当前模型的全部历史版本，是否继续？'
          ),
          h(
            'div',
            {
              style: { color: 'var(--primary-color)', cursor: 'pointer' },
              on: {
                click: () => this.downloadTextFile(needUpgradeDataSets)
              }
            },
            '点击下载合并版本的模型清单'
          )
        ])
        return message
      }
    },
    downloadTextFile(data) {
      let jsonString = data.map((item, index) => {
        let obj = {
          序号: index + 1,
          ID: item?.dataSetId,
          名称: item.displayName,
          版本号: item.versionNum
        }
        return obj
      })
      jsonString = JSON.stringify(jsonString)

      const blob = new Blob([jsonString], { type: 'application/json' })
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = '模型清单'

      document.body.appendChild(link)

      link.click()

      document.body.removeChild(link)
    },
    isTextureExist(dataSets) {
      return new Promise(resolve => {
        let validDataSets = []
        let promiseArray = []
        dataSets.forEach(item => {
          let dataSetId = item.nodeId
          let promise = getTextureList({ dataSetId })
          promiseArray.push(promise)
        })
        Promise.all(promiseArray).then(response => {
          response.forEach((item, index) => {
            if (item.isSuccess) {
              let textureList = item.data.filter(el => el.isApplied)
              if (textureList.length) {
                validDataSets.push(dataSets[index])
              }
            }
            resolve(validDataSets)
          })
        })
      })
    },
    batchSubmitConvertFiles() {
      let dataSets = this.selectDatasets
      let folders = dataSets.filter(el => el?.nodeType !== 2)
      if (folders.length) {
        this.$message.warning('文件夹不能参与转换')
        return
      }
      dataSets = dataSets.filter(el => el?.nodeType === 2)
      if (!dataSets.length) {
        this.$message.warning('请先勾选需要提交转换的文件')
        return
      }
      if (this.schemeType === 'Vector') {
        this.openVectorConfigDialog(dataSets)
        return
      }
      if (this.schemeType === 'PointCloud') {
        this.openPointCloudConfigDialog(dataSets)
        return
      }

      this.isNeedUpgrade(dataSets).then(result => {
        if (result) {
          this.isTextureExist(dataSets).then(validDataSets => {
            let dataSetNameList = validDataSets.map(el => el.nodeName)

            if (validDataSets.length) {
              // prettier-ignore
              let warningMsg = "模型包含已应用的纹理信息，提交转换后需要重新应用方可查看，确定继续转换";
              let message = this.getWarningMessage(dataSetNameList, warningMsg)
              this.$confirm(message, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
              })
                .then(() => {
                  let invalidFiles = dataSets.filter(
                    el => el?.status === 1 || el?.status === 2
                  )
                  if (invalidFiles.length) {
                    let invalidFileNames = invalidFiles.map(el => el.nodeName)
                    invalidFileNames = invalidFileNames.join(',')
                    let message = `${invalidFileNames}已在转换队列中`
                    this.$message.warning(message)
                  }
                  dataSets = dataSets.filter(
                    el => el?.status !== 1 && el?.status !== 2
                  )
                  if (dataSets.length) {
                    if (window.isLinux) {
                      this.submitConvert(dataSets)
                      return
                    }

                    this.getConfigFiles(dataSets).then(configFiles => {
                      if (configFiles.length) {
                        // prettier-ignore
                        let fileConfigInfo = this.getFileConfigInfo(configFiles);
                        this.openConfigDialog(dataSets, fileConfigInfo)
                      } else {
                        this.submitConvert(dataSets)
                      }
                    })
                  }
                })
                .catch(() => {
                  this.$message.info('取消转换')
                })
            } else {
              let invalidFiles = dataSets.filter(
                el => el?.status === 1 || el?.status === 2
              )
              if (invalidFiles.length) {
                let invalidFileNames = invalidFiles.map(el => el.nodeName)
                invalidFileNames = invalidFileNames.join(',')
                let message = `${invalidFileNames}已在转换队列中`
                this.$message.warning(message)
              }
              dataSets = dataSets.filter(
                el => el?.status !== 1 && el?.status !== 2
              )
              if (dataSets.length) {
                if (window.isLinux) {
                  this.submitConvert(dataSets)
                  return
                }

                this.getConfigFiles(dataSets).then(configFiles => {
                  if (configFiles.length) {
                    let fileConfigInfo = this.getFileConfigInfo(configFiles)
                    this.openConfigDialog(dataSets, fileConfigInfo)
                  } else {
                    this.submitConvert(dataSets)
                  }
                })
              }
            }
          })
        }
      })
    },
    submitConvertFiles(dataSets) {
      let startInterval = setInterval(() => {
        this.disabled = false
        clearInterval(startInterval)
      }, 60000)

      if (!this.disabled) {
        if (this.schemeType === 'Vector') {
          this.openVectorConfigDialog(dataSets)
          return
        }

        if (this.schemeType === 'PointCloud') {
          this.openPointCloudConfigDialog(dataSets)
          return
        }

        let dataSetInfo = dataSets[0]
        let dataSetId = dataSetInfo.nodeId
        this.isNeedUpgrade(dataSets).then(result => {
          if (!result) return

          getTextureList({ dataSetId }).then(response => {
            if (response.isSuccess) {
              let data = response.data
              let appliedData = data.filter(el => el.isApplied)
              if (appliedData.length) {
                // prettier-ignore
                let message = "检测到模型包含已应用的纹理信息，提交转换后需要重新应用方可查看，确定继续转换?";
                this.$confirm(message, '提示', {
                  confirmButtonText: '确定',
                  cancelButtonText: '取消',
                  type: 'warning'
                })
                  .then(() => {
                    if (window.isLinux) {
                      this.submitConvert(dataSets)
                      this.disabled = false
                      return
                    }
                    this.getConfigFiles(dataSets).then(configFiles => {
                      if (configFiles.length) {
                        // prettier-ignore
                        let fileConfigInfo = this.getFileConfigInfo(configFiles);
                        this.openConfigDialog(dataSets, fileConfigInfo)
                      } else {
                        this.submitConvert(dataSets)
                      }
                    })
                  })
                  .catch(() => {
                    this.$message.info('取消转换')
                  })
              } else {
                if (window.isLinux) {
                  this.submitConvert(dataSets)
                  this.disabled = false
                  return
                }

                this.getConfigFiles(dataSets).then(configFiles => {
                  if (configFiles.length) {
                    let fileConfigInfo = this.getFileConfigInfo(configFiles)
                    this.openConfigDialog(dataSets, fileConfigInfo)
                  } else {
                    this.submitConvert(dataSets)
                  }
                })
              }

              this.disabled = false
            }
          })
        })

        this.disabled = true
      }
    },
    getConfigFiles(datas) {
      return new Promise(resolve => {
        let configDatas = datas.filter(item => {
          let fileExtensName = item?.nodeName.substring(
            item?.nodeName.lastIndexOf('.') + 1
          )
          return FileTypesConfig.includes(fileExtensName)
        })
        if (configDatas.length) {
          // prettier-ignore
          if (!this.isChecked("Project.Single.Metadata.Bim.CofigConvert")) {
            this.$message.warning("存在当前环境不支持转换的文件类型，请重新选择");
            return;
          }

          let promiseArray = []
          configDatas.forEach(item => {
            let promise = this.getConvertConfig(item.nodeId)
            promiseArray.push(promise)
          })

          Promise.all(promiseArray).then(response => {
            if (response.length === promiseArray.length) {
              let configFiles = []
              response.forEach(item => {
                configFiles.push(item)
              })
              resolve(configFiles)
            }
          })
        } else {
          resolve([])
        }
      })
    },
    getConvertConfig(dataSetId) {
      return new Promise(resolve => {
        getBimConvertConfigService(dataSetId).then(response => {
          if (response.isSuccess) {
            let data = response.data
            let params = { dataSetId }
            getBimDatasetFiles(params).then(res => {
              if (res.isSuccess) {
                let resData = res.data
                let fileData = resData.items
                if (fileData.length) {
                  let fileInfo = fileData[0]
                  let config = data ? JSON.parse(data) : null
                  fileInfo.config = config
                  resolve(fileInfo)
                }
              }
            })
          }
        })
      })
    },
    getFileConfigInfo(configFiles) {
      let fileConfigInfo = {
        isModelManage: true,
        // 链接模型组不校验三维视图是否存在
        isLinkModel: false,
        isBatchConfig: configFiles.length > 1 ? true : false,
        // 文件转换配置弹窗是否展示'保留单文件配置'选项
        isCoveredVisible: configFiles.length > 1 ? true : false,
        // 文件转换配置是是否获取项目转换配置
        // 如果是模型管理且需要配置的文件数量超过两个，需要获取项目配置，根据用户是否选择'保留单文件配置'的结果
        // 决定是否保留用户的单文件配置
        isGetProjConfig: configFiles.length > 1 ? true : false
      }

      configFiles.forEach(item => {
        let fileType = ''
        if (item.fileType) {
          fileType = item.fileType.substring(item.fileType.lastIndexOf('.') + 1)
        } else {
          fileType = item.fileName.substring(item.fileName.lastIndexOf('.') + 1)
        }
        if (fileConfigInfo[fileType]) {
          fileConfigInfo[fileType].push(item)
        } else {
          fileConfigInfo[fileType] = [item]
        }
      })

      return fileConfigInfo
    },
    batchDownloadFiles() {
      let datas = this.selectDatasets
      if (!datas.length) {
        this.$message.warning('请先勾选文件')
        return
      }

      let invalidDatas = datas.filter(
        el => el?.status === 1 || el?.status === 2
      )
      if (invalidDatas.length) {
        this.$message.warning('有文件正在转换中，请稍后再试')
        return
      }

      let validFiles = datas.filter(el => el?.nodeType === 2)
      if (validFiles.length) {
        let promiseArray = []
        validFiles.forEach(item => {
          let promise = this.getFileInfo(item)
          promiseArray.push(promise)
        })

        Promise.all(promiseArray).then(response => {
          if (response.length === promiseArray.length) {
            let fileData = response
            this.customUploader.fire('fileDownload', { fileData })
          }
        })
      } else {
        this.$message.error('请选择有效的文件下载')
      }
    },
    batchMoveNode() {
      let datas = this.selectDatasets
      if (!datas.length) {
        this.$message.warning('请先勾选文件')
        return
      }
      let invalidDatas = datas.filter(
        el => el?.status === 1 || el?.status === 2
      )
      if (invalidDatas.length) {
        this.$message.warning('有文件正在转换中，请稍后再试')
        return
      }
      if (datas.length) {
        this.openMoveTreeDialog(datas)
      }
    },
    moveNodes(datas) {
      this.openMoveTreeDialog(datas)
    },
    batchDeleteNode() {
      let datas = this.selectDatasets
      if (!datas.length) {
        this.$message.warning('请先勾选文件')
        return
      }
      let invalidDatas = datas.filter(
        el => el?.status === 1 || el?.status === 2
      )
      if (invalidDatas.length) {
        this.$message.warning('有文件正在转换中，请稍后再试')
        return
      }
      if (datas.length) {
        this.deleteNodes(datas)
      }
    },
    deleteNodes(datas) {
      let schemeType = this.schemeType
      this.datasTodelete = datas

      switch (schemeType) {
        case 'Bim':
          this.deleteBimNodes(datas)
          break
        case 'Rs':
          this.deleteRsNodes(datas)
          break
        case 'Wmts':
          this.deleteWmtsNodes(datas)
          break
        case 'Osgb':
          this.deleteOsgbNodes(datas)
          break
        case 'Panorama':
          this.deletePanoramaNodes(datas)
          break
        case 'PointCloud':
          this.deletePointCloudNodes(datas)
          break
        case 'Cad':
          this.deleteCadNodes(datas)
          break
        case 'Vector':
          this.deleteVectorNodes(datas)
          break
        default:
          break
      }
    },
    onHandleFileException(events) {
      let eventInfo = events.eventInfo
      let { order, eventSource } = eventInfo
      if (eventSource !== 'modelTable') return
      if (order === 'cancel') {
        this.datasTodelete = []
        // this.$message.info("取消删除");
      } else {
        this.deleteDatasets()
      }
    },
    deleteDatasets() {
      let datas = this.datasTodelete
      let params = { nodeIds: datas.map(el => el.nodeId) }
      let schemeType = this.schemeType
      switch (schemeType) {
        case 'Bim': {
          let folderNodes = datas.filter(el => el?.nodeType === 1)
          let folderNodeIds = folderNodes.map(el => el.nodeId)
          let dataSetNodes = datas.filter(el => el?.nodeType === 2)
          let dataSetNodeIds = dataSetNodes.map(el => el.nodeId)

          if (folderNodeIds.length) {
            deleteBimFolderNode({ nodeIds: folderNodeIds }).then(response => {
              if (response.isSuccess) {
                if (dataSetNodeIds.length) {
                  deleteBimDataSetNode({ dataSetIds: dataSetNodeIds }).then(
                    res => {
                      if (res.isSuccess) {
                        this.page = 1
                        this.uploadDataList().then(total => {
                          this.datasTodelete = []
                          this.refreshCatalogTree()
                          this.updateModelTableVisible(total)
                          this.$message.success('删除成功!')
                        })
                      }
                    }
                  )
                } else {
                  this.page = 1
                  this.uploadDataList().then(total => {
                    this.datasTodelete = []
                    this.refreshCatalogTree()
                    this.updateModelTableVisible(total)
                    this.$message.success('删除成功!')
                  })
                }
              }
            })
          } else {
            deleteBimDataSetNode({ dataSetIds: dataSetNodeIds }).then(res => {
              if (res.isSuccess) {
                this.page = 1
                this.uploadDataList().then(total => {
                  this.datasTodelete = []
                  this.refreshCatalogTree()
                  this.updateModelTableVisible(total)
                  this.$message.success('删除成功!')
                })
              }
            })
          }
          break
        }
        case 'Rs':
          deleteRsNode(params).then(response => {
            if (response.isSuccess) {
              this.page = 1
              this.uploadDataList().then(total => {
                this.datasTodelete = []
                this.refreshCatalogTree()
                this.updateModelTableVisible(total)
                this.$message.success('删除成功!')
              })
            }
          })
          break
        case 'Wmts':
          deleteWmtsNode(params).then(response => {
            if (response.isSuccess) {
              this.page = 1
              this.uploadDataList().then(total => {
                this.datasTodelete = []
                this.refreshCatalogTree()
                this.updateModelTableVisible(total)
                this.$message.success('删除成功!')
              })
            }
          })
          break
        case 'Osgb':
          deleteOsgbNode(params).then(response => {
            if (response.isSuccess) {
              this.page = 1
              this.uploadDataList().then(total => {
                this.datasTodelete = []
                this.refreshCatalogTree()
                this.updateModelTableVisible(total)
                this.$message.success('删除成功!')
              })
            }
          })
          break
        case 'Panorama':
          deletePanoramaNode(params).then(response => {
            if (response.isSuccess) {
              this.page = 1
              this.uploadDataList().then(total => {
                this.datasTodelete = []
                this.refreshCatalogTree()
                this.updateModelTableVisible(total)
                this.$message.success('删除成功!')
              })
            }
          })
          break
        case 'PointCloud':
          deletePointCloudNode(params).then(response => {
            if (response.isSuccess) {
              this.page = 1
              this.uploadDataList().then(total => {
                this.datasTodelete = []
                this.refreshCatalogTree()
                this.updateModelTableVisible(total)
                this.$message.success('删除成功!')
              })
            }
          })
          break
        case 'Cad':
          deleteCadNode(params).then(response => {
            if (response.isSuccess) {
              this.page = 1
              this.uploadDataList().then(total => {
                this.datasTodelete = []
                this.refreshCatalogTree()
                this.updateModelTableVisible(total)
                this.$message.success('删除成功!')
              })
            }
          })
          break
        case 'Vector':
          deleteVectorNodeService(params).then(response => {
            if (response.isSuccess) {
              this.page = 1
              this.uploadDataList().then(total => {
                this.datasTodelete = []
                this.refreshCatalogTree()
                this.updateModelTableVisible(total)
                this.$message.success('删除成功!')
              })
            }
          })
          break
        default:
          break
      }
    },
    deleteBimNodes(datas) {
      let dataSetNodes = datas.filter(el => el?.nodeType === 2)
      let dataSetIds = dataSetNodes.map(el => el.nodeId)
      if (dataSetNodes.length) {
        deleteBimDataSetCheckService({ dataSetIds }).then(response => {
          if (response.isSuccess) {
            let data = response.data
            let dataSets = data.dataSets
            if (dataSets.length) {
              let obj = {
                eventSource: 'modelTable',
                errType: 'sceneQuoteException',
                errMsg: data.message,
                quoteData: dataSets,
                count: dataSets.length
              }
              this.$eventManage.fire('fileException', { eventInfo: obj })
            } else {
              let obj = {
                eventSource: 'modelTable',
                errType: 'deleteTip',
                errMsg: data.message
              }
              this.$eventManage.fire('fileException', { eventInfo: obj })
            }
          }
        })
      } else {
        let obj = {
          eventSource: 'modelTable',
          errType: 'deleteTip',
          errMsg: '已选数据删除后不可恢复，是否继续'
        }
        this.$eventManage.fire('fileException', { eventInfo: obj })
      }
    },
    deleteRsNodes(datas) {
      let dataSetNodes = datas.filter(el => el?.nodeType === 2)
      let dataSetIds = dataSetNodes.map(el => el.nodeId)
      if (dataSetNodes.length) {
        deleteRsDataSetCheckService({ dataSetIds }).then(response => {
          if (response.isSuccess) {
            let data = response.data
            let dataSets = data.dataSets
            if (dataSets.length) {
              let obj = {
                eventSource: 'modelTable',
                errType: 'sceneQuoteException',
                errMsg: data.message,
                quoteData: dataSets,
                count: dataSets.length
              }
              this.$eventManage.fire('fileException', { eventInfo: obj })
            } else {
              let obj = {
                eventSource: 'modelTable',
                errType: 'deleteTip',
                errMsg: data.message
              }
              this.$eventManage.fire('fileException', { eventInfo: obj })
            }
          }
        })
      } else {
        let obj = {
          eventSource: 'modelTable',
          errType: 'deleteTip',
          errMsg: '已选数据删除后不可恢复，是否继续'
        }
        this.$eventManage.fire('fileException', { eventInfo: obj })
      }
    },
    deleteWmtsNodes(datas) {
      let dataSetNodes = datas.filter(el => el?.nodeType === 2)
      let dataSetIds = dataSetNodes.map(el => el.nodeId)
      if (dataSetNodes.length) {
        deleteWmtsDataSetCheckService({ dataSetIds }).then(response => {
          if (response.isSuccess) {
            let data = response.data
            let dataSets = data.dataSets
            if (dataSets.length) {
              let obj = {
                eventSource: 'modelTable',
                errType: 'sceneQuoteException',
                errMsg: data.message,
                quoteData: dataSets,
                count: dataSets.length
              }
              this.$eventManage.fire('fileException', { eventInfo: obj })
            } else {
              let obj = {
                eventSource: 'modelTable',
                errType: 'deleteTip',
                errMsg: data.message
              }
              this.$eventManage.fire('fileException', { eventInfo: obj })
            }
          }
        })
      } else {
        let obj = {
          eventSource: 'modelTable',
          errType: 'deleteTip',
          errMsg: '已选数据删除后不可恢复，是否继续'
        }
        this.$eventManage.fire('fileException', { eventInfo: obj })
      }
    },
    deleteOsgbNodes(datas) {
      let dataSetNodes = datas.filter(el => el?.nodeType === 2)
      let dataSetIds = dataSetNodes.map(el => el.nodeId)
      if (dataSetNodes.length) {
        deleteOsgbDataSetCheckService({ dataSetIds }).then(response => {
          if (response.isSuccess) {
            let data = response.data
            let dataSets = data.dataSets
            if (dataSets.length) {
              let obj = {
                eventSource: 'modelTable',
                errType: 'sceneQuoteException',
                errMsg: data.message,
                quoteData: dataSets,
                count: dataSets.length
              }
              this.$eventManage.fire('fileException', { eventInfo: obj })
            } else {
              let obj = {
                eventSource: 'modelTable',
                errType: 'deleteTip',
                errMsg: data.message
              }
              this.$eventManage.fire('fileException', { eventInfo: obj })
            }
          }
        })
      } else {
        let obj = {
          eventSource: 'modelTable',
          errType: 'deleteTip',
          errMsg: '已选数据删除后不可恢复，是否继续'
        }
        this.$eventManage.fire('fileException', { eventInfo: obj })
      }
    },
    deletePanoramaNodes() {
      let obj = {
        eventSource: 'modelTable',
        errType: 'deleteTip',
        errMsg: '已选数据删除后不可恢复，是否继续'
      }
      this.$eventManage.fire('fileException', { eventInfo: obj })
    },
    deletePointCloudNodes(datas) {
      let dataSetNodes = datas.filter(el => el?.nodeType === 2)
      let dataSetIds = dataSetNodes.map(el => el.nodeId)
      if (dataSetNodes.length) {
        deletePointCloudDataSetCheckService({ dataSetIds }).then(response => {
          if (response.isSuccess) {
            let data = response.data
            let dataSets = data.dataSets
            if (dataSets.length) {
              let obj = {
                eventSource: 'modelTable',
                errType: 'sceneQuoteException',
                errMsg: data.message,
                quoteData: dataSets,
                count: dataSets.length
              }
              this.$eventManage.fire('fileException', { eventInfo: obj })
            } else {
              let obj = {
                eventSource: 'modelTable',
                errType: 'deleteTip',
                errMsg: data.message
              }
              this.$eventManage.fire('fileException', { eventInfo: obj })
            }
          }
        })
      } else {
        let obj = {
          eventSource: 'modelTable',
          errType: 'deleteTip',
          errMsg: '已选数据删除后不可恢复，是否继续'
        }
        this.$eventManage.fire('fileException', { eventInfo: obj })
      }
    },
    deleteCadNodes(datas) {
      let dataSetNodes = datas.filter(el => el?.nodeType === 2)
      let dataSetIds = dataSetNodes.map(el => el.nodeId)
      if (dataSetNodes.length) {
        deleteCadDataSetCheckService({ dataSetIds }).then(response => {
          if (response.isSuccess) {
            let data = response.data
            let dataSets = data.dataSets
            if (dataSets.length) {
              let obj = {
                eventSource: 'modelTable',
                errType: 'sceneQuoteException',
                errMsg: data.message,
                quoteData: dataSets,
                count: dataSets.length
              }
              this.$eventManage.fire('fileException', { eventInfo: obj })
            } else {
              let obj = {
                eventSource: 'modelTable',
                errType: 'deleteTip',
                errMsg: data.message
              }
              this.$eventManage.fire('fileException', { eventInfo: obj })
            }
          }
        })
      } else {
        let obj = {
          eventSource: 'modelTable',
          errType: 'deleteTip',
          errMsg: '已选数据删除后不可恢复，是否继续'
        }
        this.$eventManage.fire('fileException', { eventInfo: obj })
      }
    },
    deleteVectorNodes(datas) {
      let dataSetNodes = datas.filter(el => el?.nodeType === 2)
      let dataSetIds = dataSetNodes.map(el => el.nodeId)
      if (dataSetNodes.length) {
        deleteVectorDataSetCheckService({ dataSetIds }).then(response => {
          if (response.isSuccess) {
            let data = response.data
            let dataSets = data.dataSets
            if (dataSets.length) {
              let obj = {
                eventSource: 'modelTable',
                errType: 'sceneQuoteException',
                errMsg: data.message,
                quoteData: dataSets,
                count: dataSets.length
              }
              this.$eventManage.fire('fileException', { eventInfo: obj })
            } else {
              let obj = {
                eventSource: 'modelTable',
                errType: 'deleteTip',
                errMsg: data.message
              }
              this.$eventManage.fire('fileException', { eventInfo: obj })
            }
          }
        })
      } else {
        let obj = {
          eventSource: 'modelTable',
          errType: 'deleteTip',
          errMsg: '已选数据删除后不可恢复，是否继续'
        }
        this.$eventManage.fire('fileException', { eventInfo: obj })
      }
    },

    viewVersion(dataInfo) {
      if (dataInfo?.successVersion < 2) return
      let nodeId = dataInfo.nodeId
      getHistoryVersion(nodeId).then(response => {
        if (response.isSuccess) {
          let changeLogs = response.data
          let versionInfo = {
            dataSetId: dataInfo?.dataSetId,
            dataSetName: dataInfo.dataSetName,
            successVersion: dataInfo?.successVersion,
            changeLogs
          }
          // 根据数据集ID获取版本列表
          this.openModelVersionDialog(versionInfo)
        } else {
          this.$message.error(response.errMsg)
        }
      })
    },
    setCoordinate(dataInfo) {
      if (this.schemeType === 'Osgb') {
        this.openCoordinateDialog([dataInfo])
      } else {
        let dataSetIds = [dataInfo.nodeId]
        this.openCoordinateDialog(dataSetIds, dataInfo)
      }
    },
    folderBatchOperate(command, datas) {
      switch (command) {
        case '1':
          this.openMoveTreeDialog(datas)
          break
        case '2':
          console.log('复制到')
          break
        default:
          break
      }
    },
    fileBatchOperate(command, datas) {
      switch (command) {
        case '0': {
          let dataInfo = datas[0]
          this.setCoordinate(dataInfo)
          break
        }
        case '1': {
          let dataInfo = datas[0]
          this.getFileInfo(dataInfo).then(fileInfo => {
            let fileData = [fileInfo]
            this.customUploader.fire('fileDownload', { fileData })
          })
          break
        }
        case '2': {
          let dataInfo = datas[0]
          this.getFileInfo(dataInfo).then(fileInfo => {
            this.customUploader.setStatus('update')
            this.customUploader.setCurrDataSetId(dataInfo.parentId)
            fileInfo.formatType = dataInfo.formatType
            this.customUploader.setCurrFileInfo(fileInfo)
          })
          break
        }
        case '3': {
          let dataInfo = datas[0]
          if (this.schemeType === 'Bim' && dataInfo?.successVersion < 1) {
            let warningMsg = `${dataInfo?.nodeName}尚未转换成功，不能被分享`
            this.$message.warning(warningMsg)
            return
          }
          this.openShareDialog([dataInfo])
          break
        }
        case '4':
          this.openMoveTreeDialog(datas)
          break
        case '5': {
          let dataInfo = datas[0]
          this.openSetUnitDialog(dataInfo)
          break
        }
        case '6': {
          let dataInfo = datas[0]
          this.viewVersion(dataInfo)
          break
        }
        case '7': {
          let dataInfo = datas[0]
          this.openUvMapDialog(dataInfo)
          break
        }
        default:
          break
      }
    },
    downloadFile(dataInfo) {
      this.getFileInfo(dataInfo).then(fileInfo => {
        let fileData = [fileInfo]
        this.customUploader.fire('fileDownload', { fileData })
      })
    },
    getFileInfo(dataInfo) {
      return new Promise(resolve => {
        let params = { dataSetId: dataInfo.nodeId }
        switch (this.schemeType) {
          case 'Bim':
            getBimDatasetFiles(params).then(response => {
              if (response.isSuccess) {
                let data = response.data
                let fileInfo = data.items[0]
                resolve(fileInfo)
              } else {
                this.$message.error(response.errMsg)
              }
            })
            break
          case 'Rs':
            getDemDatasetFiles(params).then(response => {
              if (response.isSuccess) {
                let data = response.data
                let fileInfo = data.items[0]
                resolve(fileInfo)
              } else {
                this.$message.error(response.errMsg)
              }
            })
            break
          case 'Osgb':
            getOsgbDatasetFiles(params).then(response => {
              if (response.isSuccess) {
                let data = response.data
                let fileInfo = data.items[0]
                resolve(fileInfo)
              } else {
                this.$message.error(response.errMsg)
              }
            })
            break
          case 'Panorama':
            getPanoramaDatasetFiles(params).then(response => {
              if (response.isSuccess) {
                let data = response.data
                let fileInfo = data.items[0]
                resolve(fileInfo)
              } else {
                this.$message.error(response.errMsg)
              }
            })
            break
          case 'PointCloud':
            getPointCloudDatasetFiles(params).then(response => {
              if (response.isSuccess) {
                let data = response.data
                let fileInfo = data.items[0]
                resolve(fileInfo)
              } else {
                this.$message.error(response.errMsg)
              }
            })
            break
          case 'Cad':
            getCadDatasetFiles(params).then(response => {
              if (response.isSuccess) {
                let data = response.data
                let fileInfo = data.items[0]
                resolve(fileInfo)
              } else {
                this.$message.error(response.errMsg)
              }
            })
            break
          case 'Vector':
            getVectorDatasetFilesServices(params).then(response => {
              if (response.isSuccess) {
                let data = response.data
                let fileInfo = data.items[0]
                resolve(fileInfo)
              } else {
                this.$message.error(response.errMsg)
              }
            })
            break
        }
      })
    },
    handleVisibleChange(visible, dataInfo) {
      if (this.schemeType === 'Wmts') return

      if (visible) {
        this.$nextTick(() => {
          let uploadDom = document.getElementById(`refresh-${dataInfo.nodeId}`)
          if (uploadDom) {
            let child = uploadDom.children[0]
            if (!child.className.includes('webuploader-pick')) {
              this.customUploader.uploader.addButton({
                id: `#refresh-${dataInfo.nodeId}`,
                multiple: false
              })
            }
          }
        })
      }
    },
    handleClick(dataInfo, column) {
      if (column.label === '操作' || column.type === 'selection') return

      // if (!column.label.includes('名称')) {
      //   this.$refs.modelTable.toggleRowSelection(dataInfo)
      //   return
      // }

      if (dataInfo?.nodeType === 1) {
        this.handleCellClick(dataInfo)
        return
      }
    },
    modelView(dataInfo) {
      if (dataInfo?.nodeType === 2) {
        if (dataInfo?.status === 5 || dataInfo?.successVersion > 0) {
          this.setPageInfo()
          let projId = dataInfo.projId
          let projName = this.$route.params.projName
          projName = encodeURIComponent(projName)
          let params = `datasetId=${dataInfo.nodeId}&dataType=${this.schemeType}`
          let url = `/singleProject/datasetView/${projId}/${projName}?${params}`
          let { theme, isHeaderHidden } = this.$route.query
          if (theme) {
            url += `&theme=${theme}`
          }
          if (isHeaderHidden) {
            url += `&isHeaderHidden=false`
          }
          sessionStorage.setItem('isAuthorized', dataInfo.isAuthorized)
          this.$router.push(url)

          let schemeType = this.schemeType
          let nodeId = dataInfo.parentId
          sessionStorage.setItem(`${schemeType}NodeId`, nodeId)
        } else {
          this.$message.warning('文件尚未转换，请转换成功后再查看')
        }
      }
    },
    reCheckWmts(dataInfo) {
      let dataSetId = dataInfo?.dataSetId
      reCheckWmtsService({ dataSetId }).then(response => {
        if (response.isSuccess) {
          this.uploadDataList()
          this.clearWmtsTimer()
          this.wmtsTimer = setInterval(this.uploadDataList, 1000)
        } else {
          this.$message.error(response.errMsg)
        }
      })
    },
    setPageInfo() {
      let dataSetInfo = {
        pageNum: this.page,
        pageSize: this.size
      }
      dataSetInfo = JSON.stringify(dataSetInfo)
      sessionStorage.setItem('pageInfo', dataSetInfo)
    },
    selectChange(selection) {
      this.selectDatasets = selection
    },
    selectAll(selection) {
      this.selectDatasets = selection
    },
    /**
     * 是否为需要转换的数据类型
     */
    isConvertType() {
      return ConvertEnableDataType.includes(this.schemeType)
    },
    getColumnWidth(columnName) {
      const widthMap = {
        Bim: {
          name: '280',
          time: '240'
        },
        Rs: {
          name: '420',
          time: '340'
        },
        Wmts: {
          name: '420',
          time: '340'
        },
        Osgb: {
          name: '420',
          time: '340'
        },
        Panorama: {
          name: '420',
          time: '340'
        },
        PointCloud: {
          name: '360',
          time: '300'
        },
        Cad: {
          name: '360',
          time: '300'
        },
        Vector: {
          name: '360',
          time: '300'
        }
      }
      return widthMap[this.schemeType][columnName]
    },
    isButttonVisible(dataInfo, buttonName, fatherButtonName) {
      let visible = false
      let buttons = ButtonsList[this.schemeType]
      if (!fatherButtonName) {
        let find = buttons.find(el => el.name === buttonName)
        if (find) {
          visible =
            find?.nodeType.includes(dataInfo?.nodeType) &&
            this.isChecked(find.permissionCode)
        } else {
          visible = false
        }
      } else {
        let batchButton = buttons.find(el => el.name === fatherButtonName)
        let childrens = batchButton.children
        let find = childrens.find(el => el.name === buttonName)

        if (find) {
          visible = this.isChecked(find.permissionCode)
        } else {
          visible = false
        }
      }

      if (
        this.schemeType === 'Osgb' &&
        buttonName === 'setCoordinate' &&
        dataInfo.readonlyCoordinate
      ) {
        visible = false
      }

      return visible
    },
    isBatchButttonVisible(dataInfo, buttonName) {
      let visible = false
      let buttons = ButtonsList[this.schemeType]
      let find = buttons.find(el => el.name === buttonName)
      if (find?.nodeType.includes(dataInfo?.nodeType)) {
        let childrens = find.children
        visible = childrens.some(el => this.isChecked(el.permissionCode))
      }
      return visible
    },
    isShareBtnVisible(dataInfo, buttonName, fatherButtonName) {
      let visible = false
      visible = this.isButttonVisible(dataInfo, buttonName, fatherButtonName)
      let successVersion = dataInfo?.successVersion
      if (this.schemeType === 'Bim' && successVersion === 0) {
        visible = false
      }
      return visible
    },
    isOperateVisible() {
      let buttons = ButtonsList[this.schemeType]
      let visible = false
      visible = buttons.some(item => {
        if (item.permissionCode) {
          return this.isChecked(item.permissionCode)
        } else {
          let childrens = item.children
          return childrens.some(el => this.isChecked(el.permissionCode))
        }
      })

      return visible
    },
    isDemoProject() {
      let isDemoProj = JSON.parse(sessionStorage.getItem('isDemoProj'))
      return isDemoProj
    },
    updateConvertProgress(convertingModels) {
      let dataSetIds = convertingModels.map(el => el.nodeId)
      getProjectsConvertingProgress({ dataSetIds }).then(response => {
        if (response.isSuccess) {
          let dataList = response.data
          for (let i = 0; i < dataList.length; i++) {
            let item = dataList[i]
            if (item.ratio === -1) {
              this.uploadDataList()
              break
            }

            let ratio = Math.round(item.ratio / 100) / 100
            let find = this.dataList.find(el => el.nodeId === item?.dataSetId)

            // 用于处理转换服务停止之后再重启的情况
            let findDataset = this.convertingDataSets.find(
              el => el?.dataSetId === item?.dataSetId
            )
            if (findDataset && findDataset.isMarked && ratio === 0) {
              this.convertingDataSets = []
              this.uploadDataList()
              break
            }

            if (ratio > 0 && find?.status === 1) {
              this.uploadDataList()
              break
            }

            if (ratio === 100) {
              getProjectStatus({ dataSetId: item?.dataSetId }).then(res => {
                if (res.isSuccess) {
                  let result = res.data
                  let status = result?.status
                  if (status === 10 || status === 20) {
                    this.uploadDataList()
                    this.refreshCatalogTree()
                  } else {
                    this.$set(find, 'rate', 99)
                    // find.rate = 99;
                  }
                }
              })
              break
            } else {
              this.$set(find, 'rate', ratio)
              this.$set(find, 'rateMsg', item.message)
              // 用于处理转换服务停止之后再重启的情况
              if (ratio > 0) {
                if (!findDataset) {
                  this.convertingDataSets.push({
                    dataSetId: item?.dataSetId,
                    isMarked: true
                  })
                }
              }
              // find.rate = ratio;
            }
          }
        }
      })
    },
    refeshDom() {
      let dataList = this.dataList

      dataList.forEach(item => {
        let refreshDom = document.getElementById(`refresh-${item.nodeId}`)
        if (refreshDom) {
          let child = refreshDom.children[0]
          if (!child.className.includes('webuploader-pick')) {
            this.customUploader.uploader.addButton({
              id: `#refresh-${item.nodeId}`,
              multiple: false
            })
          }
        }
      })
    },
    getWarningMessage(dataSetNameList, messageTail) {
      let message = ''
      let messageHead = ''
      // 超过两个文件只展示前两个
      if (dataSetNameList.length > 2) {
        messageHead = `${dataSetNameList[0]}、${dataSetNameList[1]}等${dataSetNameList.length}个`
      } else {
        messageHead = dataSetNameList.join('、')
      }
      message = `${messageHead}${messageTail}`
      return message
    },
    isDisable(dataInfo) {
      let isDisable = false
      if (dataInfo?.nodeType === 2) {
        isDisable = !(dataInfo?.status === 5 || dataInfo?.successVersion > 0)
      }
      return isDisable
    },
    getIcon(dataInfo) {
      let icon = ''
      let title = ''
      let className = ''
      let isShow = false
      if (dataInfo?.nodeType === 1) {
        isShow = true
        className = 'icon'
        if (dataInfo.isAuthorized) {
          icon = 'ic-ico_folder'
        } else {
          icon = 'ic-a-wenjianzhidu1'
        }
      } else {
        let schemeType = this.schemeType
        switch (schemeType) {
          case 'Bim': {
            if (dataInfo?.hasNewFile && dataInfo?.successVersion > 0) {
              isShow = true
              icon = 'ic-jiancegengxin'
              className = 'icon-update'
              title = '当前文件有更新,提交转换后可查看最新版本'
            }
            break
          }
          case 'Panorama':
            if (dataInfo?.hasNewFile && dataInfo.hasSuccessfulConversion) {
              isShow = true
              icon = 'ic-jiancegengxin'
              className = 'icon-update'
              title = '当前文件有更新,提交转换后可查看最新版本'
            }
            break
          case 'PointCloud':
            if (dataInfo?.hasNewFile && dataInfo.hasSuccessfulConversion) {
              isShow = true
              icon = 'ic-jiancegengxin'
              className = 'icon-update'
              title = '当前文件有更新,提交转换后可查看最新版本'
            }
            break
          case 'Cad':
            if (dataInfo?.hasNewFile && dataInfo.hasSuccessfulConversion) {
              isShow = true
              icon = 'ic-jiancegengxin'
              className = 'icon-update'
              title = '当前文件有更新,提交转换后可查看最新版本'
            }
            break
          case 'Vector':
            if (dataInfo?.hasNewFile && dataInfo.hasSuccessfulConversion) {
              isShow = true
              icon = 'ic-jiancegengxin'
              className = 'icon-update'
              title = '当前文件有更新,提交转换后可查看最新版本'
            }
            break
        }
      }
      return { isShow, icon, title, className }
    },
    getFileType(dataInfo) {
      let fileType = ''
      if (dataInfo?.nodeType === 1) {
        fileType = '--'
      } else {
        fileType = dataInfo?.nodeName.substring(
          dataInfo?.nodeName.lastIndexOf('.') + 1
        )
      }

      if (dataInfo?.nodeName.includes('shp.zip')) {
        fileType = 'shp'
      }

      if (dataInfo.nodeName.includes('obj.zip')) {
        fileType = 'obj.zip'
      }
      return fileType
    },
    isRvt(dataInfo) {
      let fileType = dataInfo?.nodeName.substring(
        dataInfo?.nodeName.lastIndexOf('.') + 1
      )
      if (fileType.toLocaleLowerCase().includes('rvt')) {
        return true
      } else {
        return false
      }
    },
    getCoordinate(dataSetInfo) {
      let coordinate = '--'
      if (dataSetInfo?.coordinatesConfig) {
        let coordinatesConfig = JSON.parse(dataSetInfo?.coordinatesConfig)

        let coordinatesType = coordinatesConfig.CoordinatesType
        if (coordinatesType === 'CalibrationPoint') {
          let point = coordinatesConfig.CoordinatesPoint
          let { Latitude, Longitude } = point
          coordinate = `ENU:${Latitude},${Longitude}`
        } else {
          coordinate = coordinatesConfig.Coordinates
            ? coordinatesConfig.Coordinates
            : '--'
        }
        return coordinate
      }

      if (dataSetInfo?.coordinatesDefault) {
        if (
          this.schemeType === 'Vector' ||
          this.schemeType === 'Rs' ||
          this.schemeType === 'Wmts'
        ) {
          coordinate = dataSetInfo?.coordinatesDefault
        } else {
          coordinate = dataSetInfo?.coordinatesDefault.Coordinates
          coordinate = coordinate ? coordinate : '--'
        }

        if (this.schemeType === 'Osgb') {
          coordinate = JSON.parse(dataSetInfo?.coordinatesDefault).Coordinates
        }

        return coordinate
      }

      return coordinate
    },
    /**
     * 权限验证
     */
    isChecked(permissionCode) {
      let permissionData = Store.permissionData
      let find = permissionData.find(el => el.permissionCode === permissionCode)
      return find.isCheck
    },
    clearAllTimers() {
      this.clearProgressTimer()
      this.clearWmtsTimer()
    },
    clearProgressTimer() {
      if (this.progressTimer) {
        clearInterval(this.progressTimer)
        this.progressTimer = null
      }
    },
    clearWmtsTimer() {
      if (this.wmtsTimer) {
        clearInterval(this.wmtsTimer)
        this.wmtsTimer = null
      }
    },
    formatFileSize,
    formatTime,
    formatSecond
  }
}
</script>

<style lang="less" scoped>
@import '@/assets/css/publishSearch.less';

// .common-pagination-box {
//   background-color: var(--aside-bg-color);
// }

.common-table-box {
  height: calc(100% - 48px) !important;

  // .pagination-primary{
  //   background-color: var(--aside-bg-color);
  // }
  .operation-cell {
    :deep(.el-button) {
      line-height: normal;
      &:hover {
        line-height: normal;
      }
    }
  }

  .custom-operation {
  }
  .serialNumber {
    color: var(--table-cell-text-value-color);
    font-size: 14px;
    font-weight: 500;
  }

  // .operation-cell{
  //   .noborder-btn{
  //     &:hover{
  //       background-color: transparent;
  //       padding: 0;
  //       color: var();
  //     }
  //   }
  // }
}

.model-table-box {
  height: 100%;
  // box-shadow: var(--base-bg-dark-color32);

  .timetransfer {
    font-size: 14px;
    font-weight: 500;
    color: var(--table-cell-text-value-color);
  }
  .version {
    font-size: 14px;
    font-weight: 500;
    color: var(--table-cell-text-name-color);
  }

  .icon-column {
    display: flex;

    &-center {
      margin-top: 5px;
    }
    .content-box {
      .name {
        cursor: pointer;
        color: var(--table-cell-text-name-color);
        font-size: 14px;
        font-weight: 500;

        &:hover {
          color: var(--base-bg-light-color50);
          text-decoration: underline;
        }
      }

      .details {
        font-size: 12px;
        color: var(--table-cell-text-value-color);

        .file-type {
          margin-left: 10px;
        }
      }
    }

    .icon-update {
      margin-right: 10px;
      font-size: 16px;
      color: #1afa29;
    }
  }

  .disabled {
    .name {
      color: var(--disabled-text-color) !important;
    }

    &:hover {
      .content-box {
        .name {
          color: #909399;
          text-decoration: unset;
          cursor: not-allowed;
        }
      }
    }
  }

  .version-icon {
    margin-left: 5px;
    font-size: 15px;
    vertical-align: bottom;
  }

  .text-overflow {
    font-size: 14px;
    font-weight: 500;
    color: var(--table-cell-text-value-color);
  }

  .coordinate-box {
    display: flex;
    align-items: flex-start;

    .lock-icon-box {
      flex-shrink: 0;
      margin-left: 10px;
      font-size: 14px;
    }
  }

  .common-button-box {
    display: flex;
    justify-content: space-between;

    .upload-text-btn {
      margin-left: -10px;
    }
  }

  .status-text {
    display: flex;
    font-size: 14px;
    font-weight: 500;

    .circle {
      width: 5px;
      height: 5px;
      margin: auto 10px auto 0px;
      border-radius: 50%;
    }

    .recheck-icon {
      margin-left: 5px;
      cursor: pointer;
    }

    .bg-color1 {
      background: var(--normal-status-color);
    }

    .bg-color2 {
      background: var(--runing-status-color);
    }

    .bg-color3 {
      background: var(--error-status-color);
    }

    .bg-color4 {
      background: var(--success-status-color);
    }

    .color1 {
      color: var(--normal-status-color);
    }

    .color2 {
      color: var(--runing-status-color);
    }

    .color3 {
      color: var(--error-status-color);
    }

    .color4 {
      color: var(--success-status-color);
    }
  }

  .common-progress-box {
    .custom-text {
      .text,
      .progress-name-box,
      .arrow {
        color: var(--base-primary-bgColor) !important;
      }
    }
  }
}
</style>
